'use client';

import React, { FC, useEffect, useRef } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import Link from 'next/link';
import { Scrollbars } from 'rc-scrollbars';
import { components, OptionProps, MenuListProps, MultiValueRemoveProps, GroupBase, ControlProps, MultiValue, ActionMeta, StylesConfig } from 'react-select';
import { LoadOptions, wrapMenuList } from 'react-select-async-paginate';
import { LFLabel } from '@components/LFLabel';
import { Check } from '@components/icons';
import { LFAsyncSelect } from './LFAsyncSelect';
import { IBaseOption } from './utils';
export interface ILabelOption extends IBaseOption {
  value: string;
  color: string;
}
const defaultDropdownStyles: StylesConfig<ILabelOption, true, GroupBase<ILabelOption>> = {
  control: provided => ({
    ...provided,
    border: 0,
    height: '100%',
    background: 'transparent',
    boxShadow: 'none',
    cursor: 'pointer',
    width: '100%'
  }),
  option: (provided, state) => ({
    ...provided,
    color: 'inherit',
    background: 'inherit',
    '&:hover': {
      background: '#F7F8FB'
    },
    padding: '1.5rem 0px',
    fontWeight: state.isSelected ? '400' : '300',
    cursor: 'pointer',
    fontSize: '1.6rem'
  }),
  menu: provided => ({
    ...provided,
    zIndex: '1000'
  }),
  multiValue: provided => ({
    ...provided,
    backgroundColor: 'transparent'
  }),
  placeholder: provided => ({
    ...provided,
    color: '#727D9F',
    fontWeight: 300,
    fontSize: '1.6rem'
  }),
  multiValueRemove: () => ({}),
  valueContainer: provided => ({
    ...provided,
    fontSize: '1.6rem'
  })
};
const CustomMenuList = observer((props: MenuListProps<ILabelOption, true, GroupBase<ILabelOption>>) => {
  const intermediateRef = useRef<Scrollbars | null>(null);
  const {
    t
  } = useTranslation();
  useEffect(() => {
    if (intermediateRef.current?.view && typeof props.innerRef === 'function') {
      props.innerRef(intermediateRef.current.view as HTMLDivElement);
    }
  }, [props.innerRef]);
  return <components.MenuList {...props}>
        <Scrollbars className="custom-scrollbar" autoHeight ref={intermediateRef}>
          {props.children}
        </Scrollbars>
        <div className="mx-8 border-b border-gray-200" />
        <Link href="/settings/labels" className="cursor-default" target="_blank">
          <p className={`
              mx-12 w-fit cursor-pointer py-6 text-2xl capitalize
              hover:text-primary-300
            `}>
            {t('manageLabels')}
          </p>
        </Link>
      </components.MenuList>;
});
const MenuList = wrapMenuList(CustomMenuList);
const ControlComponent = observer((props: ControlProps<ILabelOption, true, GroupBase<ILabelOption>>) => {
  const {
    t
  } = useTranslation();
  return <label className={`
          h-full
          md:border
          ${props.isFocused ? 'border-secondary-300' : 'border-silver'}
          flex flex-col rounded-lg text-xl text-grey
          md:flex-row
        `}>
        <div className={`
            flex font-thin capitalize
            md:hidden
          `}>
          {t('labels')}
        </div>

        <div className={`
            hidden min-h-10 items-center rounded-lg
            md:flex
          `}>
          <div className={`
              flex h-full min-h-16 min-w-max items-center rounded-l-lg bg-spindle px-4 font-thin
              capitalize
            `}>
            {t('labels')}
          </div>
        </div>
        <div className={`
            w-full border
            md:border-none
            ${props.isFocused ? 'border-secondary-300' : 'border-silver'}
            ${props.isDisabled ? `
                  bg-silver
                  md:bg-white
                ` : 'bg-white'}
            overflow-hidden rounded-lg pr-3
            md:rounded-r-lg
          `}>
          <components.Control {...props} />
        </div>
      </label>;
});
const OptionComponent: FC<OptionProps<ILabelOption, true, GroupBase<ILabelOption>>> = observer(props => {
  const {
    isSelected,
    data: {
      color
    },
    label
  } = props;
  return <components.Option {...props}>
      <div className="mx-12 flex items-center gap-x-4">
        <div className={isSelected ? 'visible' : 'invisible'}>
          <Check className="h-10 w-10" />
        </div>
        <div style={{
        backgroundColor: color
      }} className="h-6 w-6 rounded-md" />
        <p>{label}</p>
      </div>
    </components.Option>;
});
const MultiValueLabel = observer((props: MultiValueRemoveProps<ILabelOption, true, GroupBase<ILabelOption>>) => {
  const {
    data: {
      color,
      label,
      value
    },
    innerProps
  } = props;
  const onClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    innerProps.onClick?.(e);
  };
  return <components.MultiValueLabel {...props}>
        <LFLabel label={{
      name: label,
      color: color,
      id: value
    }} {...innerProps.onClick && {
      onClickRemove: onClick
    }} />
      </components.MultiValueLabel>;
});
export const LFFilterLabelDropdown: FC<{
  value: ILabelOption[];
  loadOptions: LoadOptions<ILabelOption, GroupBase<ILabelOption>, {
    page: number;
  }>;
  onChange: (newValues: MultiValue<ILabelOption>, actionMeta: ActionMeta<ILabelOption>) => void;
  onMenuClose?: () => void;
  customQueryKey?: string;
}> = observer(({
  value,
  loadOptions,
  onChange,
  onMenuClose,
  customQueryKey = 'filter[labels][]'
}) => {
  const {
    t
  } = useTranslation();
  return <div className="mt-4 h-full w-full">
        <LFAsyncSelect<ILabelOption, true> label="labels" isMulti loadOptions={loadOptions} value={value} onChange={(newVal, meta) => onChange(newVal as MultiValue<ILabelOption>, meta)} onMenuClose={onMenuClose} placeholder={t('selectLabels')} customDropdownStyles={defaultStyles => ({
      ...defaultStyles,
      ...defaultDropdownStyles
    })} closeMenuOnSelect={false} hideSelectedOptions={false} customComponents={{
      Control: ControlComponent,
      MenuList,
      Option: OptionComponent,
      MultiValueLabel: () => null,
      MultiValueRemove: MultiValueLabel,
      // backwards but needed to access clear function
      DropdownIndicator: () => null,
      IndicatorSeparator: () => null,
      LoadingIndicator: () => null
    }} withDefault={false} queryParamKey={customQueryKey} />
      </div>;
});